import { Subject, ReplaySubject } from 'rxjs';

import {
  Input,
  Output,
  inject,
  Directive,
  type OnInit,
  EventEmitter,
  type OnChanges,
  type OnDestroy,
  InjectionToken,
  booleanAttribute,
} from '@angular/core';

export type TableSortDirection = 'asc' | 'desc' | '';

export type TableSortHeaderArrowPosition = 'before' | 'after';

export interface TableSortable {
  id: string;
  start: TableSortDirection;
  disableClear: boolean;
}

export interface TableSortEvent {
  active: string;
  direction: TableSortDirection;
}

export interface TableSortDefaultOptions {
  disableClear?: boolean;
  arrowPosition?: TableSortHeaderArrowPosition;
}

export const APP_TABLE_SORT_DEFAULT_OPTIONS = new InjectionToken<TableSortDefaultOptions>(
  'APP_TABLE_SORT_DEFAULT_OPTIONS',
);

@Directive({
  selector: '[appTableSort]',
  exportAs: 'appTableSort',
  host: {
    class: 'app-table-sort',
  },
})
export class TableSort implements OnChanges, OnDestroy, OnInit {
  private readonly _defaultOptions = inject(APP_TABLE_SORT_DEFAULT_OPTIONS, { optional: true });

  private readonly _initializedStream = new ReplaySubject<void>(1);

  private readonly sortables = new Map<string, TableSortable>();

  private readonly stateChangesSubject = new Subject<void>();
  public readonly stateChanges$ = this.stateChangesSubject.asObservable();

  @Input('appTableSortActive')
  active!: string;

  @Input('appTableSortStart')
  start: TableSortDirection = 'asc';

  @Input('appTableSortDirection')
  get direction(): TableSortDirection {
    return this._direction;
  }
  set direction(direction: TableSortDirection) {
    if (direction && direction !== 'asc' && direction !== 'desc' && (typeof ngDevMode === 'undefined' || ngDevMode)) {
      throw this.getSortInvalidDirectionError(direction);
    }
    this._direction = direction;
  }
  private _direction: TableSortDirection = '';

  @Input({ alias: 'appTableSortDisableClear', transform: booleanAttribute })
  public disableClear = false;

  @Input({ alias: 'appTableSortDisabled', transform: booleanAttribute })
  public disabled = false;

  @Output()
  readonly sortChange: EventEmitter<TableSortEvent> = new EventEmitter<TableSortEvent>();

  ngOnInit(): void {
    this._initializedStream.next();
  }

  ngOnChanges(): void {
    this.stateChangesSubject.next();
  }

  ngOnDestroy(): void {
    this.stateChangesSubject.complete();
    this._initializedStream.complete();
  }

  register(sortable: TableSortable): void {
    if (typeof ngDevMode === 'undefined' || ngDevMode) {
      if (!sortable.id) {
        throw this.getSortHeaderMissingIdError();
      }

      if (this.sortables.has(sortable.id)) {
        throw this.getSortDuplicateSortableIdError(sortable.id);
      }
    }

    this.sortables.set(sortable.id, sortable);
  }

  deregister(sortable: TableSortable): void {
    this.sortables.delete(sortable.id);
  }

  sort(sortable: TableSortable): void {
    if (this.active != sortable.id) {
      this.active = sortable.id;
      this.direction = sortable.start ? sortable.start : this.start;
    } else {
      this.direction = this.getNextSortDirection(sortable);
    }

    this.sortChange.emit({ active: this.active, direction: this.direction });
  }

  private getNextSortDirection(sortable: TableSortable): TableSortDirection {
    if (!sortable) {
      return '';
    }

    const disableClear = sortable?.disableClear ?? this.disableClear ?? !!this._defaultOptions?.disableClear;
    const sortDirectionCycle = getSortDirectionCycle(sortable.start || this.start, disableClear);

    let nextDirectionIndex = sortDirectionCycle.indexOf(this.direction) + 1;
    if (nextDirectionIndex >= sortDirectionCycle.length) {
      nextDirectionIndex = 0;
    }
    return sortDirectionCycle[nextDirectionIndex];
  }

  private getSortDuplicateSortableIdError(id: string): Error {
    return Error(`Cannot have two Sortables with the same id (${id}).`);
  }

  private getSortHeaderMissingIdError(): Error {
    return Error(`SortHeader must be provided with a unique id.`);
  }

  private getSortInvalidDirectionError(direction: string): Error {
    return Error(`${direction} is not a valid sort direction ('asc' or 'desc').`);
  }
}

function getSortDirectionCycle(start: TableSortDirection, disableClear: boolean): TableSortDirection[] {
  const sortOrder: TableSortDirection[] = ['asc', 'desc'];

  if (start == 'desc') {
    sortOrder.reverse();
  }

  if (!disableClear) {
    sortOrder.push('');
  }

  return sortOrder;
}
